import React from "react";
import { motion } from "framer-motion";
import { experience } from "../constant/data";

function Role({ item, idx }) {
  const Company = item.link ? "a" : "span";
  const companyProps = item.link
    ? { href: item.link, target: "_blank", rel: "noreferrer" }
    : {};

  return (
    <motion.li
      initial={{ opacity: 0, x: 60 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="relative pl-8 md:pl-10 pb-6 md:pb-8 last:pb-0"
    >
      {/* Marker on the rail */}
      <span
        className={`absolute left-0 top-7 -translate-x-1/2 w-3 h-3 rounded-full border ${
          item.current
            ? "bg-aurora-teal border-aurora-teal shadow-[0_0_14px_#2dd4bf] animate-pulseGlow"
            : "bg-ink-950 border-white/30"
        }`}
      />

      <div className="relative glass rounded-2xl p-6 md:p-8 overflow-hidden">
        {item.current && (
          <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-aurora-teal/10 blur-2xl pointer-events-none" />
        )}

        <div className="relative flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-[11px] uppercase tracking-[0.22em] text-white/55">
            {item.period}
          </span>
          {item.current && (
            <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-aurora-teal/10 border border-aurora-teal/30 text-aurora-teal">
              Current
            </span>
          )}
        </div>

        <h3 className="relative mt-3 font-jamjuree text-xl md:text-2xl font-semibold text-white leading-snug">
          {item.role}
        </h3>
        <div className="relative mt-1 text-sm text-white/65">
          <Company
            {...companyProps}
            className={item.link ? "text-aurora-teal hover:text-white transition-colors" : ""}
          >
            {item.company}
          </Company>
          <span className="text-white/35"> · {item.type}</span>
        </div>

        <p className="relative mt-4 text-sm md:text-base text-white/70 leading-relaxed max-w-2xl">
          {item.summary}
        </p>

        <ul className="relative mt-4 space-y-2 max-w-2xl">
          {item.highlights.map((h) => (
            <li key={h} className="flex gap-3 text-sm text-white/60 leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 flex-shrink-0 rounded-full bg-gradient-to-br from-aurora-teal to-aurora-magenta" />
              {h}
            </li>
          ))}
        </ul>
      </div>
    </motion.li>
  );
}

const ExperienceSection = () => {
  return (
    <section
      id="experience"
      className="relative w-full px-6 md:px-12 lg:px-24 py-16 md:py-24 overflow-hidden"
    >
      <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-aurora-magenta/10 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr,2fr] gap-10 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="eyebrow">
            <span className="dot" />
            Experience
          </span>
          <h2 className="mt-5 font-jamjuree text-3xl md:text-5xl font-semibold text-white leading-tight">
            Where I'm <span className="text-gradient-aurora">working</span> now.
          </h2>
          <p className="mt-6 text-sm md:text-base text-white/65 leading-relaxed max-w-md">
            I lead engineering at Vision Scale Labs and keep running my own
            consultancy, so every client project gets someone who ships
            production software every day.
          </p>
        </motion.div>

        <ol className="relative border-l border-white/10 ml-1.5">
          {experience.map((item, idx) => (
            <Role key={item.id} item={item} idx={idx} />
          ))}
        </ol>
      </div>
    </section>
  );
};

export default ExperienceSection;
